// Watabaga DRINK DISPENSER panel — a clocked-in worker picks a drink (water / soda) that's on the menu;
// after a 5s pour animation the server drops one into the building fridge and pays the worker $10. Only
// drinks currently on the building menu are selectable (read from foodMenuStore). world3d gates access
// (clocked in + standing at the dispenser); this just drives the selection + pour animation + the send.

import { DISPENSER_DRINKS, DISPENSE_MS, foodCost, ITEM_NAMES } from "@ghostland/shared";
import { injectTheme } from "./theme.js";
import { foodMenuStore } from "../items/foodMenuStore.js";
import { drawItemIcon } from "../items/itemArt.js";

export interface DispenserActions {
  /** A finished pour — ask the server to add one `itemId` to the fridge + pay the worker. */
  dispense: (itemId: string) => void;
}
let actions: DispenserActions | null = null;
export function setDispenserActions(a: DispenserActions): void { actions = a; }

const STYLE_ID = "ghostland-disp-style";
const CSS = `
#gl-disp-overlay { position: fixed; inset: 0; z-index: 30; display: none; align-items: center; justify-content: center;
  background: rgba(5,0,7,.55); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text); }
#gl-disp-overlay.open { display: flex; }
#gl-disp-card { width: 340px; max-width: 92vw; background: var(--gl-panel); border: 1px solid var(--gl-border);
  border-radius: 12px; box-shadow: 0 16px 50px rgba(0,0,0,.7); overflow: hidden; }
#gl-disp-head { padding: 16px 18px 12px; border-bottom: 1px solid var(--gl-border); }
#gl-disp-head h1 { margin: 0; font-size: 15px; letter-spacing: .16em; }
#gl-disp-head .hint { font-size: 11px; color: var(--gl-muted); margin-top: 4px; }
#gl-disp-body { padding: 14px 18px; display: flex; flex-direction: column; gap: 10px; }
.gl-disp-card { display: flex; align-items: center; gap: 12px; padding: 12px; border-radius: 9px;
  border: 1px solid var(--gl-border); background: var(--gl-panel-2); cursor: pointer; position: relative; overflow: hidden; }
.gl-disp-card.off { opacity: .4; cursor: default; }
.gl-disp-card:not(.off):not(.busy):hover { border-color: var(--gl-border-focus); }
.gl-disp-card canvas { width: 36px; height: 36px; image-rendering: pixelated; position: relative; }
.gl-disp-name { flex: 1; font-size: 13px; position: relative; }
.gl-disp-name small { display: block; font-size: 10px; color: var(--gl-muted); margin-top: 2px; }
.gl-disp-pay { font-size: 12px; color: #7fd08a; position: relative; }
.gl-disp-fill { position: absolute; inset: 0; background: rgba(127,208,224,.18); width: 0%; transition: none; pointer-events: none; }
#gl-disp-close { margin: 4px 18px 16px; width: calc(100% - 36px); padding: 8px 0; background: none; border: 1px solid var(--gl-border);
  border-radius: 6px; color: var(--gl-muted); cursor: pointer; font-family: inherit; font-size: 11px; }
`;

let overlay: HTMLDivElement | null = null;
let bodyEl: HTMLDivElement | null = null;
let unsub: (() => void) | null = null;
/** Active pour: the item + its rAF id, so closing cancels it cleanly (no stray dispense). */
let pour: { itemId: string; raf: number } | null = null;

/** The building's live wage for a food task (owner-adjustable), falling back to the catalogue default. */
function liveCost(itemId: string): number {
  const it = foodMenuStore.get().items.find((i) => i.itemId === itemId);
  return it ? it.cost : foodCost(itemId);
}

function render(): void {
  if (!bodyEl) return;
  const onMenu = new Set(foodMenuStore.get().items.filter((i) => i.onMenu).map((i) => i.itemId));
  bodyEl.innerHTML = "";
  for (const id of DISPENSER_DRINKS) {
    const available = onMenu.has(id);
    const busy = !!pour;
    const card = document.createElement("div");
    card.className = "gl-disp-card" + (busy && pour?.itemId !== id ? " off" : "") + (!available && !busy ? " off" : "") + (busy && pour?.itemId === id ? " busy" : "");
    const fill = document.createElement("div"); fill.className = "gl-disp-fill"; if (busy && pour?.itemId === id) fill.id = "gl-disp-active-fill";
    const icon = drawItemIcon(id); icon.style.imageRendering = "pixelated";
    const name = document.createElement("div"); name.className = "gl-disp-name";
    name.innerHTML = `${ITEM_NAMES[id as keyof typeof ITEM_NAMES] ?? id}<small>${available ? "to the fridge" : "not on the menu"}</small>`;
    const pay = document.createElement("div"); pay.className = "gl-disp-pay"; pay.textContent = `+$${liveCost(id)}`;
    card.append(fill, icon, name, pay);
    if (available && !busy) card.addEventListener("click", () => startPour(id));
    bodyEl.appendChild(card);
  }
}

function startPour(itemId: string): void {
  if (pour) return;
  pour = { itemId, raf: 0 };
  render();
  const fill = document.getElementById("gl-disp-active-fill");
  const start = performance.now();
  const step = (now: number): void => {
    const t = Math.min(1, (now - start) / DISPENSE_MS);
    if (fill) fill.style.width = `${(t * 100).toFixed(1)}%`;
    if (t >= 1) { actions?.dispense(itemId); pour = null; render(); return; } // pour done → server produces + pays
    if (pour) pour.raf = requestAnimationFrame(step);
  };
  pour.raf = requestAnimationFrame(step);
}

function ensure(): void {
  injectTheme();
  if (!document.getElementById(STYLE_ID)) { const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s); }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-disp-overlay";
  overlay.innerHTML = `<div id="gl-disp-card">
    <div id="gl-disp-head"><h1>DRINK DISPENSER</h1><div class="hint">Pick a drink to pour into the fridge (5s each). Only menu drinks can be poured.</div></div>
    <div id="gl-disp-body"></div>
    <button id="gl-disp-close">Close (Esc)</button>
  </div>`;
  document.body.appendChild(overlay);
  bodyEl = overlay.querySelector("#gl-disp-body");
  overlay.querySelector("#gl-disp-close")!.addEventListener("click", closeDispenser);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeDispenser(); });
}

export function openDispenser(): void {
  ensure();
  render();
  unsub = foodMenuStore.subscribe(render);
  overlay!.classList.add("open");
}
export function closeDispenser(): void {
  if (pour) { cancelAnimationFrame(pour.raf); pour = null; } // abandon an in-flight pour (no dispense)
  overlay?.classList.remove("open");
  unsub?.(); unsub = null;
}
export function isDispenserOpen(): boolean { return overlay?.classList.contains("open") ?? false; }
